import React, { useEffect, useState } from "react";
import AlertField from "../Components/AlertField";
import useAxiosPrivate from "../Hooks/useAxiosPrivate";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../Hooks/useAuth";
import Paginate from "../Components/Paginate";
import Records from "../Components/Records";
import InvoiceModal from "../Components/InvoiceModal";
import ConfirmationModal from "../Components/ConfirmationModal";

const AppointmentTable = () => {
  const { auth } = useAuth();
  const { state } = useLocation();
  const [alert, setAlert] = useState(state ? state : null);
  const axiosPrivate = useAxiosPrivate();
  const [list, setList] = useState({});
  const [pageCount, setpageCount] = useState(0);
  const [count, setCount] = useState(0);
  const [endValue, setEndValue] = useState(0);
  const [startValue, setStartValue] = useState(1);
  const [limit, setLimit] = useState("10");
  const [filter, setFilter] = useState("");
  const [cancelActive, setCancelActive] = useState(false);

  const [data, setData] = useState({});
  const [modalAlert, setModalAlert] = useState();
  const [alertType, setAlertType] = useState();

  const [activeTab, setActiveTab] = useState({
    all: true,
    confirm: "",
    prescribe: "",
    cancel: "",
    manual: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    let timeoutId = setTimeout(() => {
      setAlert(null);
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, []);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });

    setTimeout(() => {
      setAlert(null);
    }, 5000);
  };

  useEffect(() => {
    //gets list with limit 10 and without any query search at first load
    getInitialList(limit, activeTab, filter);
  }, []);

  const getInitialList = async (limit, activeTab, filter) => {
    try {
      //gets the list with the filter set
      const response = await axiosPrivate.get(
        `appointment/list?limit=${limit}&is_confirm=${activeTab.confirm}&is_completed=${activeTab.prescribe}&is_cancelled=${activeTab.cancel}&is_manual=${activeTab.manual}&search=${filter}`,
        {}
      );
      //sets the list of data got from api
      setList(response?.data.results);
      //gets the total record and divides by 10 to set pagination number
      setpageCount(Math.ceil(response.data.count / limit));
      //sets the total number of record to display in bottom message
      setCount(response?.data.count);
      //sets the start and end value as length of records got
      setStartValue(1);
      setEndValue(response?.data.results.length);
    } catch (err) {
      if (!err?.response) {
        showAlert("No Server Response", "warning");
      } else {
        showAlert("Load Failed", "warning");
      }
    }
  };

  const getList = async (limit, offset, filter) => {
    //it gets the value of limit to get number of records based on filter
    const res = await axiosPrivate.get(
      `appointment/list?limit=${limit}&offset=${offset}&is_confirm=${activeTab.confirm}&is_completed=${activeTab.prescribe}&is_cancelled=${activeTab.cancel}&is_manual=${activeTab.manual}&search=${filter}`,
      {}
    );
    return res;
  };

  const handlePageClick = async (data) => {
    let currentPage = data.selected * limit;
    //calls the api with limit and currentpage as offset and get response data
    const response = await getList(limit, currentPage, filter);

    //sets response into list
    setList(response?.data.results);
    //sets start and end value
    setStartValue(data.selected * limit + 1);
    setEndValue(data.selected * limit + response?.data.results.length);
    //sets 0 to start and end value if records on response is 0
    if (response?.data.results.length === 0) {
      setStartValue(0);
      setEndValue(0);
    }
  };

  const handleRecordClick = async (data) => {
    //make sure api does not hit when record value is same
    if (data.target.value !== limit) {
      //calls the api with new limit chosen with the filters
      getInitialList(data.target.value, activeTab, filter);
      setLimit(data.target.value);
    }
  };

  const onEditClick = (id, date, time) => {
    const now = new Date();
    const selectedDateTime = new Date(`${date} ${time.split("-")[0]}`);
    //validates the time to make edit available only before 1 hour but allows admin at every time
    if (
      auth?.user.roles !== 0 &&
      now.getTime() >= selectedDateTime.getTime() + 60 * 60 * 1000
    ) {
      showAlert(
        "Appointment can only be edited before 1 hour of scheduled time.",
        "warning"
      );
    } else {
      navigate(`/edit/appointment/${id}`);
    }
  };

  const onPrescribeClick = (id) => {
    navigate(`/add/prescription/${id}`);
  };

  const onCancelClick = async (id, date, time) => {
    const now = new Date();
    const selectedDateTime = new Date(`${date} ${time.split("-")[0]}`);

    //makes sure time limit is only for patient. admin can cancel at any time
    if (
      auth?.user.roles === 2 &&
      now.getTime() >= selectedDateTime.getTime() + 60 * 60 * 1000
    ) {
      showAlert(
        "Appointment can only be cancelled before 1 hour of scheduled time.",
        "warning"
      );
    } else {
      try {
        await axiosPrivate.delete(`appointment/${id}`);
        //navigating after successful cancellation
        showAlert("Appointment Cancelled.", "success");
        //updates the table
        getInitialList(limit, activeTab, filter);
      } catch (err) {
        if (!err?.response) {
          showAlert("No Server Response", "warning");
        } else if (err.response?.status === 400) {
          showAlert(err.response.data["message"], "warning");
        } else {
          showAlert("Cancellation Failed", "warning");
        }
      }
    }

    //closes the modal form
    document.getElementById(`confirmationModal${id}-close`).click();
  };

  const onTab = async (data) => {
    //default tabs
    const tabs = {
      all: "",
      confirm: "",
      prescribe: "",
      cancel: "",
      manual: "",
    };
    //sets the tab value based on button click and gets new list based on filter of tab
    if (data === "all") {
      tabs["all"] = true;
      setActiveTab(tabs);
    } else if (data === "confirm") {
      tabs["confirm"] = true;
      setActiveTab(tabs);
    } else if (data === "prescribe") {
      tabs["prescribe"] = true;
      setActiveTab(tabs);
    } else if (data === "cancel") {
      tabs["cancel"] = true;
      setActiveTab(tabs);
    } else if (data === "manual") {
      tabs["manual"] = true;
      setActiveTab(tabs);
    }
    getInitialList(limit, tabs, filter);
  };

  const onSearch = async (e) => {
    if (e.key === "Enter") {
      //replaces white space with %20
      const filter = e.target.value.replace(/\s/g, "%20");
      setFilter(filter);
      getInitialList(limit, activeTab, filter);

      //activates the reset filter button
      setCancelActive(true);
    }
  };

  const onCancel = () => {
    //hides the reset filter button
    setCancelActive(false);
    setFilter("");
    getInitialList(limit, activeTab, "");
    //sets the value of search button to null
    const inputState = document.getElementById("filterTable");
    inputState.value = "";
  };

  const showInvoice = async (appointment_id) => {
    try {
      const response = await axiosPrivate.get(
        `appointment/invoice/${appointment_id}/`
      );

      setModalAlert("");
      setAlertType("");
      setData(response?.data);
    } catch (err) {
      setModalAlert("Load Failed");
      setAlertType("warning");
      setData({});
    }
    document.getElementById("invoiceButton").click();
  };

  const activateModal = (id) => {
    //activates the modal form to update status of doctor
    document.getElementById(`confirmationModal${id}-btn-trigger`).click();
  };

  return (
    <div>
      <AlertField alert={alert} />

      <h2 className="text-center py-2">Appointments</h2>
      <div className="mx-md-5 px-md-5 mx-4">
        <div className="d-flex justify-content-between">
          <Records handleRecordClick={handleRecordClick} />
          {auth?.user.roles === 2 && (
            <a className="btn my-1 btn-primary" href="/doctor">
              Book Appointment
            </a>
          )}
        </div>
        <div className="d-flex justify-content-between flex-column-reverse flex-md-row">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`p-1 p-md-2 nav-link ${activeTab.all && "active"}`}
                onClick={() => onTab("all")}
              >
                All
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`p-1 p-md-2 nav-link ${
                  activeTab.confirm && "active"
                }`}
                onClick={() => onTab("confirm")}
              >
                Scheduled
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`p-1 p-md-2 nav-link ${
                  activeTab.prescribe && "active"
                }`}
                onClick={() => onTab("prescribe")}
              >
                Prescribed
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`p-1 p-md-2 nav-link ${
                  activeTab.cancel && "active"
                }`}
                onClick={() => onTab("cancel")}
              >
                Cancelled
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`p-1 p-md-2 nav-link ${
                  activeTab.manual && "active"
                }`}
                onClick={() => onTab("manual")}
              >
                Manual
              </button>
            </li>
          </ul>
          <div className="w-auto">
            <input
              id="filterTable"
              className="mt-2 w-100"
              type="text"
              placeholder="Name/ID and press Enter"
              onKeyUp={onSearch}
            />
            {cancelActive && (
              <button className="btn position-absolute" onClick={onCancel}>
                <i className="fas fa-xmark p-1"></i>
              </button>
            )}
          </div>
        </div>
        <div className="table-responsive">
          <table
            data-toggle="table"
            className="table table-light table-striped table-hover"
            data-pagination="true"
            data-search="true"
          >
            <thead>
              <tr>
                <th scope="col">ID</th>
                {auth?.user.roles !== 1 && <th scope="col">Doctor's Name</th>}
                {auth?.user.roles !== 2 && <th scope="col">Patient's Name</th>}
                <th scope="col">Reason</th>
                <th scope="col">Date of Visit</th>
                <th scope="col">Time</th>
                <th scope="col">Prescription</th>
                <th scope="col">Status</th>
                {auth?.user.roles !== 1 && <th scope="col">Invoice</th>}
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {list?.length ? (
                list.map((list, i) => (
                  <tr key={i}>
                    <td>{list.appointment_id}</td>
                    {auth?.user.roles !== 1 && (
                      <td>{list?.doctor_id.user.name}</td>
                    )}
                    {auth?.user.roles !== 2 && <td>{list?.patient_name}</td>}
                    <td>{list?.reason}</td>
                    <td>{list?.appointment_date}</td>
                    <td>{list?.appointment_time}</td>
                    <td>
                      {list?.is_completed ? (
                        <a href={`/view/prescription/${list?.id}`}>View</a>
                      ) : (
                        "-"
                      )}
                    </td>

                    {list?.is_confirm ? (
                      <td className="text-info">Scheduled</td>
                    ) : list.is_cancelled ? (
                      <td className="text-danger">Cancelled</td>
                    ) : list?.is_completed ? (
                      <td className="text-success">Completed</td>
                    ) : list?.is_manual ? (
                      <td className="text-warning">Payment Left</td>
                    ) : (
                      "-"
                    )}

                    {auth?.user.roles !== 1 && (
                      <td className="ps-3">
                        <button
                          className="btn p-0 m-0 text-center"
                          onClick={() => showInvoice(list.id)}
                        >
                          <i className="fa-solid m-0 text-primary fa-eye"></i>
                        </button>
                      </td>
                    )}
                    <td>
                      {auth?.user.roles !== 1 && (
                        <button
                          className="mx-2 btn py-0 btn-primary"
                          onClick={() =>
                            onEditClick(
                              list?.id,
                              list?.appointment_date,
                              list?.appointment_time
                            )
                          }
                          disabled={list?.is_cancelled || list?.is_completed}
                        >
                          Edit
                        </button>
                      )}
                      {auth?.user.roles !== 1 && (
                        <>
                          <button
                            className="btn py-0 btn-danger"
                            onClick={() => activateModal(list?.id)}
                            disabled={list?.is_cancelled || list?.is_completed}
                          >
                            Cancel
                          </button>
                          <ConfirmationModal
                            button="Delete"
                            target={`confirmationModal${list?.id}`}
                            btnClass="d-none"
                            title="Cancel Appointment Confirmation"
                            body={`Are you sure you want to cancel appointment with Dr. ${list?.doctor_id.user.name} scheduled on ${list?.appointment_date} at ${list?.appointment_time}?`}
                            close="No"
                            save="Yes"
                            onClick={() =>
                              onCancelClick(
                                list?.id,
                                list?.appointment_date,
                                list?.appointment_time
                              )
                            }
                          />
                        </>
                      )}
                      {auth?.user.roles === 1 && (
                        <button
                          className="btn py-0 btn-primary"
                          onClick={() => onPrescribeClick(list?.id)}
                          disabled={
                            list?.is_completed ||
                            list?.is_cancelled ||
                            list?.is_manual
                          }
                        >
                          Prescribe
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center">
                    No Appointments
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Paginate
          startValue={startValue}
          endValue={endValue}
          count={count}
          pageCount={pageCount}
          handlePageClick={handlePageClick}
        />
      </div>
      <InvoiceModal data={data} message={modalAlert} type={alertType} />
    </div>
  );
};

export default AppointmentTable;
